import { format, getTime, formatDistanceToNow } from "date-fns";

export const normalizeData = (data) => {
  // following code credits goes to roelbeerens see: https://forum.strapi.io/t/discussion-regarding-the-complex-response-structure-for-rest-graphql-developer-experience/13400/9
  const isObject = (data) =>
    Object.prototype.toString.call(data) === "[object Object]";

  const flatten = (data) => {
    if (!data.attributes) return data;

    return {
      id: data.id,
      ...data.attributes,
    };
  };

  if (Array.isArray(data)) {
    return data.map((item) => normalizeData(item));
  }

  if (isObject(data)) {
    if (Array.isArray(data.data)) {
      data = [...data.data];
    } else if (isObject(data.data)) {
      data = flatten({ ...data.data });
    } else if (data.data === null) {
      data = null;
    } else {
      data = flatten(data);
    }

    for (const key in data) {
      data[key] = normalizeData(data[key]);
    }

    return data;
  }

  return data;
};

export const updateFieldsWithRecordValues = (record, setValue) => {
  Object.keys(record).forEach((key) => {
    // if key is array or object, we update it
    if (
      record[key] &&
      (Array.isArray(record[key]) || typeof record[key] === "object")
    ) {
      // console.log("Updating", key);
      setValue(key, record[key]);
    }
  });
};

export const applyRecordToFields = (record, setValue) => {
  Object.keys(record).forEach((key) => {
    // console.log("Updating", key);
    setValue(key, record[key]);
  });
};

export const truncateString = (str, num) => {
  if (str.length <= num) {
    return str;
  }
  return str.slice(0, num) + "...";
};

export const separatePrimitiveAndObjectFields = (values) => {
  // separate primitives from arrays and objects
  let primitives = {};
  let arraysAndObjects = {};
  Object.keys(values).forEach((key) => {
    if (
      typeof values[key] === "boolean" ||
      typeof values[key] === "number" ||
      typeof values[key] === "string" ||
      values[key] === null ||
      values[key] === undefined ||
      values[key] instanceof Date
    ) {
      primitives[key] = values[key];
    } else {
      arraysAndObjects[key] = values[key];
    }
  });
  return { primitives, arraysAndObjects };
};

// ----------------------------------------------------------------------

export function fDate(date) {
  return format(new Date(date), "dd MMMM yyyy");
}

export function fDateTime(date) {
  return format(new Date(date), "dd MMM yyyy p");
}

export function fTimestamp(date) {
  return getTime(new Date(date));
}

export function fDateTimeSuffix(date) {
  return format(new Date(date), "dd/MM/yyyy hh:mm p");
}

export function fToNow(date) {
  return formatDistanceToNow(new Date(date), {
    addSuffix: true,
  });
}

// Function to calculate the difference in days between two dates
export function daysBetweenDates(date1, date2) {
  // Convert both dates to milliseconds
  var date1Ms = date1.getTime();
  var date2Ms = date2.getTime();

  // Calculate the difference in milliseconds
  var timeDifference = date2Ms - date1Ms;

  // Convert the time difference to days
  var daysDifference = Math.floor(timeDifference / (1000 * 60 * 60 * 24));

  return daysDifference;
}

export const translateLocaleObj = function (obj, lang) {
  const DEBUG = false;
  const _ = require("lodash");

  const originalObj = normalizeData(_.cloneDeep(obj));
  if (DEBUG) console.log("Original, ", originalObj);

  const fillValidData = (obj, template, translateStrings = false) => {
    let newObj = normalizeData(_.cloneDeep(obj));
    // apply keys that don't exist in the translation
    Object.keys(template).forEach((key) => {
      if (newObj[key] == null) {
        newObj[key] = template[key];
      }
    });
    // loop through the attributes and assign the values
    Object.keys(template).forEach((key) => {
      if (template[key] != null) {
        if (typeof template[key] === "object") {
          // if it is an object, we check if it is an array
          if (Array.isArray(template[key])) {
            // if the object has values, we pick it
            if (template[key].length > newObj[key].length) {
              if (DEBUG)
                console.log(
                  "Defaulting to template (array) ",
                  key,
                  template[key]
                );
              newObj[key] = template[key];
            }
          } else {
            // if the object has values, we pick it
            if (Object.keys(template[key]).length > 0) {
              if (DEBUG)
                console.log(
                  "Defaulting to template (object), ",
                  key,
                  template[key]
                );
              newObj[key] = template[key];
            }
          }
        } else if (typeof template[key] === "boolean") {
          // if the object has values, we pick it
          if (template[key] === true) {
            newObj[key] = template[key];
            if (DEBUG)
              console.log(
                "Defaulting to template (bool), ",
                key,
                template[key]
              );
          }
        }
        // we ignore the string values if they are already translated
        if (translateStrings && typeof template[key] === "string") {
          if (DEBUG)
            console.log(
              "Defaulting to template (string), ",
              key,
              template[key]
            );
          newObj[key] = template[key];
        }
      }
    });
    if (DEBUG) console.log("Translated:", newObj);
    return newObj;
  };

  // prune nested localizations
  if (obj.localizations && obj.localizations.data) {
    obj.localizations.data.forEach((localization) => {
      delete localization.attributes.localizations;
    });
  } else if (obj.localizations) {
    obj.localizations.forEach((localization) => {
      delete localization.localizations;
    });
  }

  // flatten objects
  let flatObj = normalizeData({ ...obj });
  // identify the language to translate to
  let translated = flatObj.locale === lang ? { ...flatObj } : null;
  let flatTranslated = translated ? translated : null;

  if (DEBUG) console.log("Step 1", translated);
  // start localization
  if (
    flatTranslated === null &&
    flatObj.localizations !== null &&
    flatObj.localizations !== undefined
  ) {
    // loop through localizations
    if (flatObj.localizations && flatObj.localizations.length > 0) {
      if (DEBUG) console.log("Step 2", flatObj.localizations);
      //find localized data
      let localizedTemplate = flatObj.localizations.find(
        (l) => l.locale === lang
      );
      if (DEBUG) console.log("Step 4", localizedTemplate);
      if (localizedTemplate != null) {
        // build the object from relevant data
        if (DEBUG) console.log("Initial translated", originalObj);

        flatTranslated = fillValidData(originalObj, localizedTemplate, true);

        if (DEBUG) console.log("Step 5", flatTranslated);
        return flatTranslated;
      } else {
        if (DEBUG)
          if (DEBUG)
            console.log("No matching localization found for this object");
        return flatObj;
      }
    } else {
      // we have no matching localization, return the original object
      if (DEBUG) console.log("No matching localizations");
      return flatObj;
    }
  } else {
    // if we already have the localization, return it
    if (DEBUG) console.log("Already translated");
    // check for empty or defaults
    flatTranslated = { ...originalObj };
    if (!originalObj.localizations) return flatTranslated;
    let localizedTemplate = originalObj.localizations.find(
      (l) => l.locale !== originalObj.locale
    );
    if (!localizedTemplate) return flatTranslated;
    if (DEBUG) console.log("Step 6", flatTranslated, localizedTemplate);
    flatTranslated = fillValidData(flatTranslated, localizedTemplate);
    return flatTranslated;
  }
};

export const defaultValue = (value, control, defaultVal) => {
  if (defaultVal == null) return "";
  return control != null && control[value] != null
    ? control[value]
    : defaultVal[value];
};
